import React from "react";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { fmtDay } from "@/lib/utils";

const MEAL_ORDER = ["Breakfast", "Lunch", "Snacks", "Dinner"];
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const buildHtml = ({ base, date, totals, orders }) => {
  const meals = [...new Set(orders.map((o) => o.delivery_time))].sort((a, b) => MEAL_ORDER.indexOf(a) - MEAL_ORDER.indexOf(b));
  const totalRows = totals.map((t) => `<tr><td class="box"></td><td>${esc(t.item_name)}</td><td class="num">${t.total_qty}</td><td>${esc(t.unit)}</td></tr>`).join("");
  const mealBlocks = meals.map((m) => {
    const list = orders.filter((o) => o.delivery_time === m).sort((a, b) => (a.delivery_exact_time || "").localeCompare(b.delivery_exact_time || ""));
    const cards = list.map((o) => `
      <div class="card">
        <div class="card-h"><b>${esc(o.cafeteria_name)}</b><span>${esc(o.delivery_date ? fmtDay(o.delivery_date) : "")} ${esc(o.delivery_exact_time || "")}</span></div>
        <table>${o.items.map((it) => `<tr><td class="box"></td><td>${esc(it.item_name)}</td><td class="num">${it.qty}</td><td>${esc(it.unit)}</td></tr>`).join("")}</table>
        ${o.remarks ? `<p class="rem">Note: ${esc(o.remarks)}</p>` : ""}
      </div>`).join("");
    return `<h2>${esc(m)} <small>(${list.length} order${list.length === 1 ? "" : "s"})</small></h2><div class="grid">${cards}</div>`;
  }).join("");
  return `<!doctype html><html><head><title>Prep Sheet ${esc(base)} ${esc(date || "all")}</title><style>
    body{font-family:Arial,sans-serif;color:#111;margin:24px;font-size:13px}
    header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #ea580c;padding-bottom:10px;margin-bottom:16px}
    header img{height:44px} h1{font-size:22px;margin:0} h2{font-size:16px;margin:22px 0 8px;border-bottom:1px solid #ccc;padding-bottom:4px}
    small{color:#666;font-weight:normal} table{width:100%;border-collapse:collapse} td,th{border:1px solid #bbb;padding:6px 8px;text-align:left}
    th{background:#fde7d9} .num{text-align:right;font-weight:bold;font-size:15px} .box{width:18px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px} .card{border:1px solid #999;border-radius:6px;padding:8px;break-inside:avoid}
    .card-h{display:flex;justify-content:space-between;margin-bottom:6px} .card td{padding:4px 6px} .rem{font-style:italic;margin:6px 0 0}
    .sign{margin-top:28px;display:flex;gap:40px} .sign div{flex:1;border-top:1px solid #333;padding-top:4px;color:#555}
    @media print{body{margin:10mm}}
  </style></head><body>
    <header><img src="${window.location.origin}/baron-logo.jpg" alt="Baron Kitchen"/><div style="text-align:right"><h1>Kitchen Prep Sheet — ${esc(base)}</h1>
    <div>Delivery date: <b>${esc(date ? fmtDay(date) : "All dates")}</b> · ${orders.length} pending order(s) · Printed ${esc(new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }))}</div></div></header>
    <h2>Batch Cook Totals</h2>
    <table><thead><tr><th class="box">✓</th><th>Item</th><th class="num">Total Qty</th><th>Unit</th></tr></thead><tbody>${totalRows}</tbody></table>
    ${mealBlocks ? `<h2 style="border:none;margin-bottom:0">Packing by Cafeteria</h2>${mealBlocks}` : ""}
    <div class="sign"><div>Prepared by</div><div>Checked by</div><div>Time</div></div>
    <script>window.onload=function(){setTimeout(function(){window.print()},300)}</script>
  </body></html>`;
};

export default function PrepSheetButton({ base, date, totals, orders }) {
  const pending = orders.filter((o) => o.status === "Preparing");
  const print = () => {
    if (!totals.length) return toast.error("Nothing pending to cook for this date");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Allow pop-ups to print the prep sheet");
    w.document.write(buildHtml({ base, date, totals, orders: pending }));
    w.document.close();
  };
  return (
    <Button variant="outline" className="gap-2" onClick={print} data-testid="kitchen-print-prep-sheet-button">
      <Printer className="h-4 w-4" /> Print Prep Sheet{date ? ` · ${fmtDay(date)}` : ""}
    </Button>
  );
}
